import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  FetchStatusType,
  StatusType,
} from '../../react/hooks/entity/status_type';
import type { StoreEntityKey } from '../entities/types';
import type {
  SerializableEndpointError,
  SerializableError,
} from '../serializable_error';

export type EntityMeta = {
  status: StatusType;
  fetchStatus: FetchStatusType;
  fetchedAt: number;
  error?: SerializableEndpointError | SerializableError;
};

export type EntitiesMetaState = {
  [key in StoreEntityKey]: {
    [id: string]: EntityMeta;
  };
};

export const defaultEntityMeta: EntityMeta = {
  status: 'pending',
  fetchStatus: 'idle',
  fetchedAt: 0,
};

const initialState: EntitiesMetaState = {
  acl_templates: {},
  application_tokens: {},
  approvals: {},
  approval_requests: {},
  applications: {},
  asset_persons: {},
  asset_relations: {},
  assets: {},
  automations: {},
  collection_keyframes: {},
  collections: {},
  custom_actions: {},
  discoveries: {},
  discoveries_admin: {},
  export_locations: {},
  file_sets: {},
  files: {},
  formats: {},
  groups: {},
  groups_basic: {},
  identity_providers: {},
  jobs: {},
  keyframes: {},
  logos: {},
  metadata_fields: {},
  metadata_unmerged_views: {},
  metadata_user_fields: {},
  metadata_views: {},
  search_views: {},
  notifications: {},
  person_instances: {},
  persons: {},
  playlists: {},
  portals: {},
  projects: {},
  proxies: {},
  recycle_bin_file_sets: {},
  recycle_bin_formats: {},
  relation_types: {},
  role_groups: {},
  role_groups_basic: {},
  saved_search_results: {},
  saved_searches: {},
  search_backends: {},
  search_history: {},
  search_items: {},
  segments: {},
  share_users: {},
  shares: {},
  storages: {},
  storage_gateways: {},
  storage_gateway_clusters: {},
  system_domains: {},
  system_domains_basic: {},
  system_logs_recipients: {},
  teams: {},
  teams_basic: {},
  themes: {},
  transcoders: {},
  transcription_properties: {},
  users: {},
  users_basic: {},
  recent_search_items: {},
  webhooks: {},
  subtitles: {},
};

export const entitiesMetaSlice = createSlice({
  name: 'entitiesMeta',
  initialState,
  reducers: {
    updateEntityFetchedAt: (
      state,
      action: PayloadAction<{
        entityType: StoreEntityKey;
        ids: string[];
        fetchedAt?: number;
      }>,
    ) => {
      for (const id of action.payload.ids) {
        state[action.payload.entityType][id] = {
          status: 'success',
          fetchStatus: 'idle',
          fetchedAt: action.payload.fetchedAt ?? Date.now(),
        };
      }
    },

    updateEntityMeta: (
      state,
      action: PayloadAction<{
        entityType: StoreEntityKey;
        id: string;
        meta: Partial<EntityMeta>;
      }>,
    ) => {
      const entityState = state[action.payload.entityType];
      if (!entityState[action.payload.id]) {
        entityState[action.payload.id] = defaultEntityMeta;
      }
      entityState[action.payload.id] = {
        ...entityState[action.payload.id],
        ...action.payload.meta,
      };
    },
  },
});

export const { updateEntityFetchedAt, updateEntityMeta } =
  entitiesMetaSlice.actions;

export const entitiesMetaReducer = entitiesMetaSlice.reducer;
